import { z } from "zod";
import { dashboardFieldKeySchema, dashboardMetricAggregateSchema, dashboardRecordWidgetBaseSchema, dashboardWidgetBaseSchema, dashboardWidgetSchema, recordSortSchema, } from "./channels.js";
/*
 * The widget types a channel's Overview has beyond a dashboard's (docs/specs/shared-widget-blocks.md,
 * section 1 and phase 6): the Twin home's goal rings, progress, countdown, status list, streak and ranked
 * bars. They are a channel widget's only, never a dashboard view's, so a dashboard definition older
 * clients parse is unchanged. Each is evaluated from the dashboards' own computations and drawn by the
 * block of the same name. A target or a date it needs and was not given leaves it unconfigured, saying so.
 */
const measureSchema = z.object({
    aggregate: dashboardMetricAggregateSchema,
    fieldKey: dashboardFieldKeySchema.optional(),
}).strict();
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "a date as YYYY-MM-DD");
export const STATUS_TONES = ["neutral", "good", "warning", "bad"];
export const channelOnlyWidgetSchema = z.discriminatedUnion("type", [
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("progress"),
        metric: measureSchema,
        /** What the measure counts towards; null until someone sets it. */
        target: z.number().finite().positive().nullable().default(null),
        /** The figures' unit in words: "invoices", "GBP". */
        unit: z.string().trim().min(1).max(24).optional(),
    }).strict(),
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("goal_rings"),
        goals: z.array(z.object({
            label: z.string().trim().min(1).max(80),
            metric: measureSchema,
            /** The goal; null until someone sets it. */
            target: z.number().finite().positive().nullable().default(null),
            /** Whether more is better (up) or less is (down). */
            direction: z.enum(["up", "down"]).default("up"),
        }).strict()).min(1).max(6),
    }).strict(),
    dashboardWidgetBaseSchema.extend({
        type: z.literal("countdown"),
        /** The day counted down to; null until someone sets it. */
        date: isoDateSchema.nullable().default(null),
        /** A key date after it, shown beneath. */
        next: z.object({ date: isoDateSchema, label: z.string().trim().min(1).max(80) }).strict().optional(),
    }).strict(),
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("status_list"),
        /** The field that names each row; left out, each row is named by its record's title. */
        nameFieldKey: dashboardFieldKeySchema.optional(),
        statusFieldKey: dashboardFieldKeySchema,
        detailFieldKey: dashboardFieldKeySchema.optional(),
        /** The tone each status reads in; a status not named is neutral. */
        tones: z.record(z.string().trim().min(1).max(80), z.enum(STATUS_TONES)).default({}),
        sort: recordSortSchema.optional(),
        limit: z.number().int().min(1).max(20).default(8),
    }).strict(),
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("streak"),
        /** The date that puts a record on a day. */
        dateFieldKey: dashboardFieldKeySchema,
        /** Whole weeks, ending with this one. */
        weeks: z.number().int().min(4).max(17).default(12),
    }).strict(),
    dashboardRecordWidgetBaseSchema.extend({
        type: z.literal("ranked_bars"),
        metric: measureSchema,
        dimension: z.object({ fieldKey: dashboardFieldKeySchema }).strict(),
        limit: z.number().int().min(1).max(12).default(6),
    }).strict(),
]);
export const CHANNEL_ONLY_WIDGET_TYPES = channelOnlyWidgetSchema.options.map((option) => option.shape.type.value);
/** Any widget a channel may hold: a dashboard's types, and the Overview's own. */
export const channelWidgetSchema = z.discriminatedUnion("type", [...dashboardWidgetSchema.options, ...channelOnlyWidgetSchema.options]);
/** What a widget of this type may hold: the schema its spec is checked against. */
export function widgetSpecSchema(type) {
    const schema = channelWidgetSchema.options.find((option) => option.shape.type.value === type);
    if (!schema)
        throw new Error(`No spec schema for widget type ${type}`);
    return schema;
}
export function isChannelOnlyWidget(widget) {
    return CHANNEL_ONLY_WIDGET_TYPES.includes(widget.type);
}
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/u;
/** Whether a YYYY-MM-DD is a day in the calendar: 2026-02-31 has the shape of one and is not. */
function realDay(value) {
    const match = DAY.exec(value);
    if (!match)
        return false;
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}
/** Whether a period's start or end is one a widget is bounded by: a year, a month, a day, or an instant on a real day. */
function realPeriodEdge(value) {
    if (/^\d{4}$/u.test(value))
        return true;
    if (/^\d{4}-\d{2}$/u.test(value))
        return realDay(`${value}-01`);
    if (DAY.test(value))
        return realDay(value);
    return /^\d{4}-\d{2}-\d{2}T/u.test(value) && realDay(value.slice(0, 10)) && !Number.isNaN(Date.parse(value));
}
/**
 * What is wrong with a widget as written, beyond its shape: the rules a dashboard's widget has always been
 * held to (a bridge's order, what a saved or a live summary holds), dates that are days in the calendar, and
 * a period that can be read. Checked when a widget is saved (a change's spec), so none of these is found
 * out later on a tile; a widget already stored is read as it is.
 */
export function channelWidgetProblems(widget) {
    const problems = [];
    if (!isChannelOnlyWidget(widget)) {
        // The union above is built from the dashboard schema's options, which leaves that schema's own rules behind.
        const checked = dashboardWidgetSchema.safeParse(widget);
        if (!checked.success)
            problems.push(...checked.error.issues.map((issue) => issue.message));
    }
    if (widget.type === "countdown") {
        for (const date of [widget.date, widget.next?.date]) {
            if (date && !realDay(date))
                problems.push(`${date} is not a day in the calendar.`);
        }
    }
    for (const [edge, value] of [["start", widget.dateContext?.periodStart], ["end", widget.dateContext?.periodEnd]]) {
        if (value !== undefined && !realPeriodEdge(value)) {
            problems.push(`The period's ${edge}, "${value}", is not a date: give a year (2026), a month (2026-03) or a day (2026-03-31).`);
        }
    }
    return problems;
}
const currencyCodeSchema = z.string().regex(/^[A-Z]{3}$/u);
/** What each of these types evaluates to: the figures its block is drawn from. */
export const channelOnlyWidgetValueSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("progress"), value: z.number(), target: z.number(), share: z.number().min(0), unit: z.string().optional(),
        /** The currency its measure is an amount in, where it is one: from the data set, never guessed from the unit. */
        currency: currencyCodeSchema.optional(),
    }).strict(),
    z.object({
        type: z.literal("goal_rings"),
        goals: z.array(z.object({
            label: z.string(), value: z.number(), target: z.number(), direction: z.enum(["up", "down"]),
            /** How far the goal is met, 0 to 1. */
            progress: z.number().min(0).max(1),
            currency: currencyCodeSchema.optional(),
        }).strict()),
    }).strict(),
    z.object({
        type: z.literal("countdown"), date: isoDateSchema, days: z.number().int(),
        next: z.object({ date: isoDateSchema, label: z.string(), days: z.number().int() }).strict().optional(),
    }).strict(),
    z.object({
        type: z.literal("status_list"),
        rows: z.array(z.object({ key: z.string(), name: z.string(), status: z.string(), tone: z.enum(STATUS_TONES), detail: z.string().optional() }).strict()),
    }).strict(),
    z.object({
        type: z.literal("streak"),
        /** Each day from the first Monday to today, as the UTC midnight it starts at, with how many records fell on it. */
        days: z.array(z.object({ date: z.number().int(), count: z.number().int().nonnegative() }).strict()),
        today: z.number().int(),
    }).strict(),
    z.object({
        type: z.literal("ranked_bars"),
        rows: z.array(z.object({ key: z.string(), label: z.string(), value: z.number() }).strict()),
        /** The one currency every row is an amount in, where they are amounts. */
        currency: currencyCodeSchema.optional(),
    }).strict(),
]);
